'use client';

import { skills } from '@/constants';
import { cn, handleFormSubmitApiError } from '@/lib/utils';
import { volunteerSchema } from '@/lib/validationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import FormSuccess from '../blocks/FormSuccess';
import Button from '../ui/Button';
import ErrorText from '../ui/ErrorText';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import ToggleSwitch from '../ui/ToggleSwitch';
import { postData } from '@/lib/apiHelpers';

type FormData = z.infer<typeof volunteerSchema>;

export default function VolunteerForm({ className }: { className?: string }) {
    const t = useTranslations();
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [activePageIndex, setActivePageIndex] = useState<number>(0);
    const [selectedSkillValue, setSelectedSkillValue] = useState<string | undefined>('');
    const [nlChecked, setNlChecked] = useState<boolean>(false);
    const [prevVolunteerChecked, setPrevVolunteerCheckedChecked] = useState<boolean>(false);

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(volunteerSchema),
    });

    const onSubmit = async (formData: FormData) => {
        const {
            name,
            lastName,
            email,
            streetAddress,
            city,
            province,
            postalCode,
            country,
            phone,
            occupation,
            language,
            additionalLanguages,
            availability,
            otherAvailabilities,
            whyWouldYouLike,
            isThereAnything,
        } = formData;

        setSubmitting(true);

        try {
            postData('/form/volunteer-form', {
                name,
                lastName,
                email,
                streetAddress,
                city,
                province,
                postalCode,
                country,
                phone,
                occupation,
                language,
                additionalLanguages,
                availability,
                otherAvailabilities,
                whyWouldYouLike,
                isThereAnything,
                yourSkills: selectedSkillValue,
                previousVolunteer: prevVolunteerChecked,
                receiveNewsletter: nlChecked,
            });
            setIsSuccessful(true);
            reset();
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSkillsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSkillValue(event.target.value);
    };

    const handleReceiveNlCheck = (event: React.ChangeEvent<HTMLInputElement>) =>
        setNlChecked(event.target.checked);

    const handlePrevVolunteerCheck = (event: React.ChangeEvent<HTMLInputElement>) =>
        setPrevVolunteerCheckedChecked(event.target.checked);

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    return isSuccessful ? (
        <FormSuccess className="w-full" onClickBack={() => setIsSuccessful(false)} />
    ) : (
        <form
            className={cn('volunteer-form-trigger flex flex-col relative w-full', className)}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="flex flex-col gap-7">
                <span className="font-tradeGothic font-bold text-[48px] lg:text-h3 uppercase leading-none">
                    {t('Volunteer.beAPart')}
                </span>

                {/* step 1 */}
                <div className={`${activePageIndex === 0 ? 'block' : 'hidden'}`}>
                    <p className="font-helveticaNeue font-light text-sm lg:text-p pb-7 border-b border-black">
                        {t('Volunteer.inIsLooking')}
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-5 mt-7">
                        <Input
                            {...register('name')}
                            type="text"
                            name="name"
                            id="name"
                            placeholder={t('Form.name')}
                            autoComplete="false"
                            required
                            error={getFieldError('name')}
                        />
                        <Input
                            {...register('lastName')}
                            type="text"
                            name="lastName"
                            id="lastName"
                            placeholder={t('Form.lastName')}
                            autoComplete="false"
                            required
                            error={getFieldError('lastName')}
                        />
                        <Input
                            {...register('email')}
                            type="email"
                            name="email"
                            id="email"
                            placeholder={t('Form.email')}
                            autoComplete="false"
                            required
                            error={getFieldError('email')}
                        />
                        <Input
                            {...register('streetAddress')}
                            type="text"
                            name="streetAddress"
                            id="streetAddress"
                            placeholder={t('Form.streetAddress')}
                            autoComplete="false"
                            error={getFieldError('streetAddress')}
                        />
                        <Input
                            {...register('city')}
                            type="text"
                            name="city"
                            id="city"
                            placeholder={t('Form.city')}
                            autoComplete="false"
                            required
                            error={getFieldError('city')}
                        />
                        <Input
                            {...register('province')}
                            type="text"
                            name="province"
                            id="province"
                            placeholder={t('Form.province')}
                            autoComplete="false"
                            required
                            error={getFieldError('province')}
                        />
                        <Input
                            {...register('postalCode')}
                            type="number"
                            name="postalCode"
                            id="postalCode"
                            placeholder={t('Form.postalCode')}
                            autoComplete="false"
                            required
                            error={getFieldError('postalCode')}
                        />
                        <Input
                            {...register('country')}
                            type="text"
                            name="country"
                            id="country"
                            placeholder={t('Form.country')}
                            autoComplete="false"
                            required
                            error={getFieldError('country')}
                        />
                        <Input
                            {...register('phone')}
                            type="phone"
                            name="phone"
                            id="phone"
                            placeholder={t('Form.phone')}
                            autoComplete="false"
                            required
                            error={getFieldError('phone')}
                        />
                        <Input
                            {...register('occupation')}
                            type="text"
                            name="occupation"
                            id="occupation"
                            placeholder={t('Form.occupation')}
                            autoComplete="false"
                            error={getFieldError('occupation')}
                        />
                    </div>
                </div>

                {/* step 2 */}
                <div className={`${activePageIndex === 1 ? 'block' : 'hidden'}`}>
                    <p className="font-helveticaNeue font-light text-sm lg:text-p pb-7 border-b border-black">
                        {t('Volunteer.inIsInternational')}
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-5 mt-7">
                        <Input
                            {...register('language')}
                            type="text"
                            name="language"
                            id="language"
                            placeholder={t('Form.language')}
                            autoComplete="false"
                            error={getFieldError('language')}
                        />
                        <Input
                            {...register('additionalLanguages')}
                            type="text"
                            name="additionalLanguages"
                            id="additionalLanguages"
                            placeholder={t('Form.additionalLanguages')}
                            autoComplete="false"
                            error={getFieldError('additionalLanguages')}
                        />
                        <Select
                            {...register('yourSkills')}
                            name="yourSkills"
                            id="yourSkills"
                            value={selectedSkillValue}
                            onChange={handleSkillsChange}
                            options={skills(t)}
                            placeholder={t('Form.yourSkills')}
                            autoComplete="false"
                        />
                    </div>
                    <div className="flex flex-col gap-5 my-7">
                        <span className="font-tradeGothic font-bold text-md lg:text-h4 uppercase leading-none">
                            {t('Volunteer.previousExperience')}
                        </span>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-5">
                            <div className="flex flex-col gap-2 max-lg:w-full">
                                <span className="font-helveticaNeue font-light text-sm lg:text-p">
                                    {t('Volunteer.previousVolunteer')}
                                </span>
                                <ToggleSwitch
                                    {...register('previousVolunteer')}
                                    onChange={handlePrevVolunteerCheck}
                                    name="previousVolunteer"
                                    id="previousVolunteer"
                                />
                            </div>
                            <Input
                                {...register('availability')}
                                type="text"
                                name="availability"
                                id="availability"
                                placeholder={t('Form.availability')}
                                autoComplete="false"
                                error={getFieldError('availability')}
                            />
                        </div>
                        <p className="font-helveticaNeue font-light text-sm lg:text-p">
                            {t('Volunteer.inIsActive')}
                        </p>
                        <Textarea
                            {...register('otherAvailabilities')}
                            wrapperClassName="w-full"
                            name="otherAvailabilities"
                            id="otherAvailabilities"
                            placeholder={t('Form.otherAvailabilities')}
                            autoComplete="false"
                            rows={1}
                        />
                    </div>
                </div>

                {/* step 3 */}
                <div className={`${activePageIndex === 2 ? 'block' : 'hidden'}`}>
                    <p className="font-helveticaNeue font-light text-sm lg:text-p pb-7 border-b border-black">
                        {t('Volunteer.inIsInternational')}
                    </p>
                    <div className="flex flex-col gap-5 mt-7">
                        <span className="font-tradeGothic font-bold text-md lg:text-h4 uppercase leading-none">
                            {t('Buttons.more')}
                        </span>
                        <Textarea
                            {...register('whyWouldYouLike')}
                            wrapperClassName="w-full"
                            name="whyWouldYouLike"
                            id="whyWouldYouLike"
                            placeholder={t('Form.whyWouldYouLike')}
                            autoComplete="false"
                            rows={1}
                        />
                        <div className="flex flex-col gap-2 max-lg:w-full">
                            <span className="font-helveticaNeue font-light text-sm lg:text-p">
                                {t('Volunteer.doYouWantToReceiveNewsletter')}
                            </span>
                            <ToggleSwitch
                                {...register('receiveNewsletter')}
                                onChange={handleReceiveNlCheck}
                                name="receiveNewsletter"
                                id="receiveNewsletter"
                            />
                        </div>
                        <Textarea
                            {...register('isThereAnything')}
                            wrapperClassName="w-full mb-[60px]"
                            name="isThereAnything"
                            id="isThereAnything"
                            placeholder={t('Form.isThereAnything')}
                            autoComplete="false"
                            rows={1}
                        />
                    </div>
                </div>
            </div>

            {errors && (
                <div className="flex flex-col gap-1 text-left h-12 pt-6">
                    <ErrorText error={getFieldError('name')} />
                    <ErrorText error={getFieldError('lastName')} />
                    <ErrorText error={getFieldError('email')} />
                    <ErrorText error={getFieldError('streetAddress')} />
                    <ErrorText error={getFieldError('city')} />
                    <ErrorText error={getFieldError('province')} />
                    <ErrorText error={getFieldError('postalCode')} />
                    <ErrorText error={getFieldError('country')} />
                    <ErrorText error={getFieldError('phone')} />
                    <ErrorText error={getFieldError('occupation')} />
                    <ErrorText error={getFieldError('language')} />
                    <ErrorText error={getFieldError('additionalLanguages')} />
                    <ErrorText error={getFieldError('yourSkills')} />
                    <ErrorText error={getFieldError('previousVolunteer')} />
                    <ErrorText error={getFieldError('availability')} />
                    <ErrorText error={getFieldError('otherAvailabilities')} />
                    <ErrorText error={getFieldError('whyWouldYouLike')} />
                    <ErrorText error={getFieldError('receiveNewsletter')} />
                    <ErrorText error={getFieldError('isThereAnything')} />
                </div>
            )}

            <div className="flex items-center justify-between">
                <Button
                    className={`p-5 border border-primary border-solid ${activePageIndex === 0 && 'opacity-30'}`}
                    variant="ghost"
                    title={t('Buttons.back')}
                    type="button"
                    disabled={activePageIndex === 0}
                    onClick={() => setActivePageIndex((prev) => Math.max(prev - 1, 0))}
                />

                <span className="font-bold text-h6 text-primary text-nowrap">{`${activePageIndex + 1} / 3`}</span>

                <Button
                    className={`self-end p-5 ${activePageIndex === 2 ? 'block' : 'hidden'}`}
                    variant="secondary"
                    title={t('Buttons.submit')}
                    type="submit"
                    submitting={submitting}
                    disabled={submitting}
                />
                <Button
                    className={`p-5 border border-primary border-solid ${activePageIndex !== 2 ? 'block' : 'hidden'}`}
                    variant="ghost"
                    title={t('Buttons.next')}
                    type="button"
                    onClick={() => setActivePageIndex((prev) => Math.min(prev + 1, 2))}
                />
            </div>
        </form>
    );
}
